import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * `tailwind-merge`, taught the Sanctuary scales.
 *
 * Out of the box it knows Tailwind's own spacing (`p-4`, `p-8`) and would treat
 * `p-md` and `p-xl` as unrelated arbitrary classes — so a caller passing
 * `className="p-xl"` to a component whose base is `p-md` would get BOTH, and
 * whichever CSS rule happened to come later would win. Silent, and impossible to
 * debug from the markup.
 *
 * The preset's custom scales are therefore declared here: spacing (2xs…3xl),
 * radius, font sizes, shadows and the dialog/sidebar widths. Everything else —
 * colours, which are ordinary token names — merges correctly already.
 *
 * **Adding a step to ANY of these scales means adding it HERE, in the same
 * change.** P10.2a added `2xs` to `tailwind.preset.js` and not to this list, and
 * the result was exactly the silent double-class the paragraph above describes:
 * `cn('mt-2xs', 'mt-md')` kept both. It happened again in P10.2b with the type
 * scale (`text-code`), which is why `cn.util.spec.ts` now pins every step of the
 * spacing **and** the font-size scale, so the next one cannot be added quietly.
 *
 * Pinned to tailwind-merge v2: v3 targets Tailwind v4's class syntax and
 * mis-merges under the v3.4 this app builds with.
 */
/** Every step in the preset's `spacing`, `safe-b` included — no exceptions, so there is no rule to remember. */
const SPACING = ['2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', 'safe-b'];
const RADIUS = ['sm', 'md', 'lg', 'xl', 'full'];
const TEXT = ['hero', 'h1', 'h2', 'h3', 'h4', 'body', 'label', 'caption', 'tiny', 'code'];
const SHADOW = ['sm', 'md', 'lg', 'xl'];
const WIDTH = [
  'dialog-sm',
  'dialog-md',
  'dialog-lg',
  'dialog-xl',
  'sidebar',
  'sidebar-collapsed',
  'media-tile',
];
/**
 * The preset's `maxHeight` names — nine of them since P25b2.
 *
 * Not live the day it was declared (no component composes two of them yet), and
 * that is exactly why it is here: this list is the thing that stops the NINTH
 * being added quietly, and an overlay that ends up with both `max-h-dialog` and
 * `max-h-value-block` would be clamped by whichever CSS rule happened to come
 * later — silent, and impossible to debug from the markup.
 */
const MAX_H = [
  'dialog',
  'sheet-mobile',
  'popover-available',
  'dropdown-available',
  'select-available',
  'value-block',
  'unsaved-summary',
  'pane-rail',
  'slot-day',
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // Sanctuary spacing, on every property Tailwind derives from the scale.
      p: [{ p: SPACING }],
      px: [{ px: SPACING }],
      py: [{ py: SPACING }],
      ps: [{ ps: SPACING }],
      pe: [{ pe: SPACING }],
      pt: [{ pt: SPACING }],
      pb: [{ pb: SPACING }],
      m: [{ m: SPACING }],
      mx: [{ mx: SPACING }],
      my: [{ my: SPACING }],
      ms: [{ ms: SPACING }],
      me: [{ me: SPACING }],
      mt: [{ mt: SPACING }],
      mb: [{ mb: SPACING }],
      gap: [{ gap: SPACING }],
      'gap-x': [{ 'gap-x': SPACING }],
      'gap-y': [{ 'gap-y': SPACING }],
      'font-size': [{ text: TEXT }],
      rounded: [{ rounded: RADIUS }],
      'rounded-s': [{ 'rounded-s': RADIUS }],
      'rounded-e': [{ 'rounded-e': RADIUS }],
      shadow: [{ shadow: SHADOW }],
      w: [{ w: WIDTH }],
      'max-h': [{ 'max-h': MAX_H }],
      // `scroll-mt-section` (P10.3f) merges against the spacing scale's own
      // `scroll-mt-*`, so both names have to be in one group or a caller
      // overriding the section offset would keep both.
      'scroll-mt': [{ 'scroll-mt': [...SPACING, 'section'] }],
    },
  },
});

/**
 * The one class-name helper. Every component composes its base classes with the
 * caller's `className` through this, so a caller can always override and never
 * has to guess which rule wins.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
